import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { ADMIN_NAV } from "@/pages/admin/AdminHome";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Award, Loader2, Pencil, Check } from "lucide-react";

const BADGE = {
  Certified: "bg-emerald-100 text-emerald-800",
  Expired: "bg-amber-100 text-amber-800",
  "Not certified": "bg-slate-100 text-slate-500",
};

export function AdminCertifications() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [val, setVal] = useState("");

  const load = () => api.get("/admin/certifications").then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const save = async (uid) => {
    try {
      await api.patch(`/admin/users/${uid}/id`, { employee_id: val });
      setRows((r) => r.map((x) => (x.user_id === uid ? { ...x, employee_id: val } : x)));
      setEditing(null);
      toast.success("Employee ID updated.");
    } catch { toast.error("Couldn't update the ID."); }
  };

  const certified = rows?.filter((r) => r.status === "Certified").length ?? 0;

  return (
    <DashboardShell title="Instructor Certifications" subtitle="TSB Benchmark certification roster — faculty & advisors" nav={ADMIN_NAV}>
      {!rows && <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#003057]" /></div>}
      {rows && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Card className="p-5 card-elevate"><p className="text-xs font-bold uppercase tracking-widest text-slate-400">Staff</p><p className="font-mono-num text-2xl font-bold text-[#003057]">{rows.length}</p></Card>
            <Card className="p-5 card-elevate"><p className="text-xs font-bold uppercase tracking-widest text-slate-400">Certified</p><p className="font-mono-num text-2xl font-bold text-[#003057]">{certified}</p></Card>
            <Card className="p-5 card-elevate"><p className="text-xs font-bold uppercase tracking-widest text-slate-400">Not certified</p><p className="font-mono-num text-2xl font-bold text-[#003057]">{rows.length - certified}</p></Card>
          </div>
          <Card className="p-2 card-elevate sm:p-4">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>Employee ID</TableHead><TableHead>Score</TableHead>
                <TableHead>Issued</TableHead><TableHead>Valid through</TableHead><TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.user_id} data-testid={`cert-row-${r.user_id}`}>
                    <TableCell>
                      <p className="font-semibold text-[#003057]">{r.name}</p>
                      <p className="text-xs text-slate-400">{r.email} · {r.role}</p>
                    </TableCell>
                    <TableCell>
                      {editing === r.user_id ? (
                        <span className="flex items-center gap-1">
                          <Input value={val} onChange={(e) => setVal(e.target.value)} className="h-8 w-28" data-testid={`cert-id-input-${r.user_id}`} />
                          <Button size="sm" variant="ghost" onClick={() => save(r.user_id)} data-testid={`cert-id-save-${r.user_id}`}><Check className="h-4 w-4 text-emerald-600" /></Button>
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5">
                          <span className="font-mono-num text-sm text-slate-700">{r.employee_id || "—"}</span>
                          <button onClick={() => { setEditing(r.user_id); setVal(r.employee_id || ""); }} data-testid={`cert-id-edit-${r.user_id}`} className="text-slate-300 hover:text-[#005291]"><Pencil className="h-3.5 w-3.5" /></button>
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono-num text-sm">{r.score != null ? `${r.score}%` : "—"}</TableCell>
                    <TableCell className="font-mono-num text-xs text-slate-500">{r.issued_at?.slice(0, 10) || "—"}</TableCell>
                    <TableCell className="font-mono-num text-xs text-slate-500">{r.expires_at?.slice(0, 10) || "—"}</TableCell>
                    <TableCell><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${BADGE[r.status]}`}>{r.status}</span></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>
      )}
    </DashboardShell>
  );
}
